import { useEffect } from 'react';

/**
 * Custom hook to prevent copy-paste functionality during evaluations
 * This hook adds event listeners to prevent:
 * - Keyboard shortcuts (Ctrl+C, Ctrl+V, Ctrl+X, Ctrl+A)
 * - Right-click context menu
 * - Drag and drop operations
 * - Copy, cut, and paste events
 */
export const useCopyPastePrevention = () => {
  useEffect(() => {
    const showWarning = () => {
      // Create a temporary warning element
      const warning = document.createElement('div');
      warning.style.cssText = `
        position: fixed;
        top: 20px;
        right: 20px;
        background: #ff4444;
        color: white;
        padding: 10px 15px;
        border-radius: 5px;
        font-size: 14px;
        z-index: 10000;
        box-shadow: 0 2px 10px rgba(0,0,0,0.3);
        animation: slideIn 0.3s ease-out;
      `;
      warning.textContent = '⚠️ Copier-coller désactivé pendant l\'évaluation';
      
      // Add animation styles
      const style = document.createElement('style');
      style.textContent = `
        @keyframes slideIn {
          from { transform: translateX(100%); opacity: 0; }
          to { transform: translateX(0); opacity: 1; }
        }
      `;
      document.head.appendChild(style);
      document.body.appendChild(warning);
      
      // Remove warning after 3 seconds
      setTimeout(() => {
        if (warning.parentNode) {
          warning.parentNode.removeChild(warning);
        }
        if (style.parentNode) {
          style.parentNode.removeChild(style);
        }
      }, 3000);
    };

    const preventCopyPaste = (e) => {
      // Prevent Ctrl+C, Ctrl+V, Ctrl+X, Ctrl+A (and Cmd+ on Mac)
      if ((e.ctrlKey || e.metaKey) && 
          (e.key === 'c' || e.key === 'v' || e.key === 'x' || e.key === 'a')) {
        e.preventDefault();
        e.stopPropagation();
        showWarning();
        return false;
      }
      
      // Prevent right-click context menu on the entire evaluation card
      if (e.type === 'contextmenu') {
        const target = e.target;
        const isEvaluationCard = target.closest('.evaluation-card') ||
                                target.closest('.question-content') ||
                                target.closest('.answer-section') ||
                                target.closest('.question-header') ||
                                target.closest('.question-meta') ||
                                target.closest('.security-notice');
        
        if (isEvaluationCard) {
          e.preventDefault();
          e.stopPropagation();
          showWarning();
          return false;
        }
        // Allow right-click on other elements (header, footer, navigation, etc.)
        return true;
      }
      
      // Don't interfere with other events (like clicks)
      return true;
    };

    const preventDragDrop = (e) => {
      e.preventDefault();
      e.stopPropagation();
      showWarning();
      return false;
    };

    // Add event listeners to the entire document
    document.addEventListener('keydown', preventCopyPaste, true);
    document.addEventListener('contextmenu', preventCopyPaste, true);
    document.addEventListener('dragstart', preventDragDrop, true);
    document.addEventListener('drop', preventDragDrop, true);
    document.addEventListener('paste', preventCopyPaste, true);
    document.addEventListener('copy', preventCopyPaste, true);
    document.addEventListener('cut', preventCopyPaste, true);

    return () => {
      document.removeEventListener('keydown', preventCopyPaste, true);
      document.removeEventListener('contextmenu', preventCopyPaste, true);
      document.removeEventListener('dragstart', preventDragDrop, true);
      document.removeEventListener('drop', preventDragDrop, true);
      document.removeEventListener('paste', preventCopyPaste, true);
      document.removeEventListener('copy', preventCopyPaste, true);
      document.removeEventListener('cut', preventCopyPaste, true);
    };
  }, []);
}; 